import { useState } from "react";
import type { ChangeEvent, DragEvent } from "react";
import { Delete, DeleteOutline } from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { createEmptyRow, type FeatureSheetRow } from "../config/featureRows";

interface FeatureRowsEditorProps {
  /** Current rows (controlled by the parent). */
  rows: FeatureSheetRow[];
  /** Called with the next rows array on any add/edit/delete/reorder. */
  onRowsChange: (rows: FeatureSheetRow[]) => void;
  /** Disable all inputs/controls (e.g. while fetching or saving). */
  disabled?: boolean;
  /** Row ids to highlight (e.g. rows changed by a background refetch). */
  highlightedRowIds?: Set<number>;
  /** Whether the per-row delete controls are shown. */
  showDelete?: boolean;
  /** Toggle callback; required for `showDelete` to be controllable. */
  onShowDeleteChange?: (next: boolean) => void;
}

/**
 * Controlled, presentational editor for a feature sheet as ordered key/value
 * rows: paired Feature/Value text fields, Add Row, a Delete-rows toggle, and
 * drag-to-reorder. It owns no data — the parent holds the rows and persists
 * them. Shared by PartsAIFeatureSheet (run feature sheet) and EditPartModal.
 */
const FeatureRowsEditor = ({
  rows,
  onRowsChange,
  disabled = false,
  highlightedRowIds,
  showDelete,
  onShowDeleteChange,
}: FeatureRowsEditorProps) => {
  const [draggedRowId, setDraggedRowId] = useState<number | null>(null);
  // Uncontrolled fallback: used only when the parent doesn't pass `showDelete`.
  const [internalShowDelete, setInternalShowDelete] = useState(false);
  const showDeleteIcons = showDelete ?? internalShowDelete;
  const toggleShowDelete = () => {
    const next = !showDeleteIcons;
    if (onShowDeleteChange) onShowDeleteChange(next);
    else setInternalShowDelete(next);
  };

  const handleInputChange =
    (index: number, field: "feature" | "value") =>
    (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      onRowsChange(
        rows.map((row, rowIndex) =>
          rowIndex === index ? { ...row, [field]: value } : row,
        ),
      );
    };

  const handleAddRow = () => {
    onRowsChange([...rows, createEmptyRow()]);
  };

  const handleDeleteRow = (rowId: number) => {
    onRowsChange(rows.filter((row) => row.__rowId !== rowId));
  };

  const handleDragStart = (rowId: number) => {
    setDraggedRowId(rowId);
  };

  const handleDragOver = (e: DragEvent<HTMLTableRowElement>) => {
    if (disabled || draggedRowId === null) return;
    e.preventDefault();
    e.currentTarget.style.opacity = "0.5";
  };

  const handleDragLeave = (e: DragEvent<HTMLTableRowElement>) => {
    if (disabled || draggedRowId === null) return;
    e.currentTarget.style.opacity = "1";
  };

  const handleDrop = (
    e: DragEvent<HTMLTableRowElement>,
    targetRowId: number,
  ) => {
    if (disabled || draggedRowId === null) return; // no row drag in progress
    e.preventDefault();
    e.currentTarget.style.opacity = "1";

    if (draggedRowId === targetRowId) {
      setDraggedRowId(null);
      return;
    }

    const draggedIndex = rows.findIndex((row) => row.__rowId === draggedRowId);
    const targetIndex = rows.findIndex((row) => row.__rowId === targetRowId);
    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedRowId(null);
      return;
    }

    const newRows = [...rows];
    const [draggedRow] = newRows.splice(draggedIndex, 1);
    newRows.splice(targetIndex, 0, draggedRow);
    onRowsChange(newRows);
    setDraggedRowId(null);
  };

  return (
    <Box>
      <Table size="small" sx={{ mb: 2 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, backgroundColor: "grey.50" }}>
              Feature
            </TableCell>
            <TableCell sx={{ fontWeight: 600, backgroundColor: "grey.50" }}>
              Value
            </TableCell>
            {showDeleteIcons && (
              <TableCell
                align="center"
                sx={{ fontWeight: 600, backgroundColor: "grey.50", width: 80 }}
              >
                Delete
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => (
            <TableRow
              key={row.__rowId}
              draggable={!disabled}
              onDragStart={() => handleDragStart(row.__rowId)}
              onDragEnd={(e) => {
                e.currentTarget.style.opacity = "1";
                setDraggedRowId(null);
              }}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, row.__rowId)}
              sx={{
                cursor: disabled ? "default" : "grab",
                transition: "all 0.2s",
                backgroundColor: highlightedRowIds?.has(row.__rowId)
                  ? "rgba(255, 193, 7, 0.08)"
                  : "transparent",
                "&:hover": { backgroundColor: "action.hover" },
              }}
            >
              <TableCell>
                <TextField
                  fullWidth
                  size="small"
                  value={row.feature}
                  placeholder="e.g., Material"
                  onChange={handleInputChange(index, "feature")}
                  disabled={disabled}
                  variant="outlined"
                />
              </TableCell>
              <TableCell>
                <TextField
                  fullWidth
                  size="small"
                  value={row.value}
                  placeholder="e.g., Stainless Steel"
                  onChange={handleInputChange(index, "value")}
                  disabled={disabled}
                  variant="outlined"
                />
              </TableCell>
              {showDeleteIcons && (
                <TableCell align="center">
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteRow(row.__rowId)}
                    aria-label={`Delete row ${row.feature || row.__rowId}`}
                    color="error"
                    disabled={disabled}
                    sx={{
                      backgroundColor: "rgba(211, 47, 47, 0.08)",
                      "&:hover": {
                        backgroundColor: "rgba(211, 47, 47, 0.15)",
                      },
                    }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Stack direction="row" gap={1}>
        <Button
          variant="outlined"
          size="small"
          onClick={handleAddRow}
          disabled={disabled}
          data-attr="parts-ai:btn:add-feature-row"
        >
          Add Row
        </Button>
        <Button
          variant="outlined"
          size="small"
          color="error"
          startIcon={<DeleteOutline />}
          onClick={toggleShowDelete}
          disabled={disabled}
          data-attr="parts-ai:btn:toggle-delete-rows"
        >
          {showDeleteIcons ? "Hide Delete" : "Delete Rows"}
        </Button>
      </Stack>
    </Box>
  );
};

export default FeatureRowsEditor;
